import type { ComponentType, ReactNode } from 'react'
import type { OverviewModule } from '../api/devices'
import { NotYetMoved } from './bridge'
import { DeviceAttached } from './DeviceAttached'
import { DeviceConfiguration } from './DeviceConfiguration'
import { DeviceOverview } from './DeviceOverview'
import { DevicePerformance } from './DevicePerformance'
import { DevicePrinters } from './DevicePrinters'
import { DeviceSummary } from './DeviceSummary'
import { DeviceAgentSettings } from './DeviceAgentSettings'
import { DeviceAgentModules } from './DeviceAgentModules'
import { DeviceCmdb } from './DeviceCmdb'
import { DeviceHardwareEvents } from './DeviceHardwareEvents'
import { DeviceProperties } from './DeviceProperties'
import { DeviceSettings } from './DeviceSettings'
import { DeviceSoftwareLicenses } from './DeviceSoftwareLicenses'
import { DeviceSubSettings } from './DeviceSubSettings'
import { DeviceWarranty } from './DeviceWarranty'
import { DeviceSoftseeSummary } from './DeviceSoftseeSummary'
import { DeviceSoftseeEvents } from './DeviceSoftseeEvents'
import { DeviceSoftseeUsedApps } from './DeviceSoftseeUsedApps'
import { DeviceSoftseeOs } from './DeviceSoftseeOs'
import { DeviceSoftseeApps } from './DeviceSoftseeApps'
import { DeviceOpteemumInstall, DeviceSoftseeInstall } from './DeviceSoftseeInstall'
import { DeviceOpteemumUpdates, DeviceSoftseeUpdates } from './DeviceSoftseeUpdates'
import { DeviceSoftseeUninstall } from './DeviceSoftseeUninstall'
import { DeviceSoftseePackages } from './DeviceSoftseePackages'
import { DeviceOpteemumSummary } from './DeviceOpteemumSummary'
import { DeviceOsManagement } from './DeviceOsManagement'
import { DeviceDriverErrors } from './DeviceDriverErrors'
import { DeviceStartupApps } from './DeviceStartupApps'
import { DeviceStartupServices } from './DeviceStartupServices'
import { DeviceRemoteEventViewer } from './DeviceRemoteEventViewer'
import { DeviceOpteemumEvents } from './DeviceOpteemumEvents'
import { DeviceProactiviteeSummary } from './DeviceProactiviteeSummary'
import { AgentlessDetails } from '../inventoree/AgentlessDetails'
import { DeviceUpload } from './DeviceUpload'
import { DeviceAgentless } from './DeviceAgentless'
import { DeviceQuickDetails } from './DeviceQuickDetails'
import { DeviceSoftseeQuickDetails } from './DeviceSoftseeQuickDetails'
import { DeviceEndpointPerformanceSettings } from './DeviceEndpointPerformanceSettings'
import { DeviceCmd } from './DeviceCmd'
import { DeviceDiagnosisRepair, DeviceRestoreOptimize, DeviceSetupProtection, DeviceTuneUp, DeviceVirusRemoval } from './DeviceAiActions'
import { DeviceAiTodo } from './DeviceAiTodo'
import { DeviceSystemAlarms } from './DeviceSystemAlarms'
import { DeviceProactiviteeEvents } from './DeviceProactiviteeEvents'
import { DeviceProactiviteeEventViewer } from './DeviceProactiviteeEventViewer'
import { DeviceExternalMonitoring } from './DeviceExternalMonitoring'
import { DeviceActiveApps } from './DeviceActiveApps'
import { DeviceProcessLoad } from './DeviceProcessLoad'
import { DeviceSessions } from './DeviceSessions'
import { DeviceExtendedMonitoring } from './DeviceExtendedMonitoring'
import { DeviceConfigRelay } from './DeviceConfigRelay'
import { DeviceSystemPerformance } from './DeviceSystemPerformance'
import { DevicePerformanceManagement } from './DevicePerformanceManagement'
import { DeviceNetworkStatus } from './DeviceNetworkStatus'
import { DeviceNeighborWatch } from './DeviceNeighborWatch'
import { DeviceConnectionQuality } from './DeviceConnectionQuality'
import { DeviceCoreServices } from './DeviceCoreServices'
import { DeviceRemoteOps } from './DeviceRemoteOps'
import { DeviceWatch } from './DeviceWatch'
import { DeviceDiskManagement } from './DeviceDiskManagement'
import { DeviceUac } from './DeviceUac'
import { REMOTE_CONTROL_CATEGORIES, remoteControlCategory } from '../easeedesk/TicketRemoteCategory'

/**
 * Ecranele fisei, pe cheia lor din meniu (adresa din portal fara numarul
 * masinii). Ce nu e inca aici se deschide in portalul de acum, prin punte
 * (bridge.tsx). Fiecare ecran mutat primeste un rand aici si e trecut in
 * v4/MIGRATION.md.
 */

export interface ScreenProps {
  id: number
  /** Modulul din care s-a deschis fisa (`inventoree`, `opteemum`, ...). */
  from: string
  overview: OverviewModule[]
  /** Un alt ecran al fisei, desenat inauntrul acestuia (Overview isi cere rezumatele modulelor asa). */
  screen: (href: string, label: string) => ReactNode
  /** Deschide alta fila a fisei, in pagina, si aprinde optiunea ei din meniu (legaturile `more` de pe casute). */
  open: (href: string) => void
  /**
   * Are fisa asta fila ceruta? Meniul se schimba de la o masina la alta (module,
   * drepturi, familia de sistem), deci un ecran care trimite spre alta fila
   * intreaba intai - exact ce raspundea `odOpenDeviceSheet` din
   * device_workspace.js prin `false`. Lipseste acolo unde ecranul e desenat
   * fara fisa in jur (probe, fisa unei masini agentless).
   */
  hasTab?: (href: string) => boolean
  /**
   * Tichetul din care e deschis ecranul (fila Remote Control a tichetului).
   * Scrierile care pleaca de aici il pun pe comanda (`it_comenzi.ticket_id`) si
   * in jurnalul tichetului, cum facea portalul; lipseste pe fisa aparatului.
   */
  ticket?: number
}

export const SCREENS: Record<string, ComponentType<ScreenProps>> = {
  'inventoree/deviceOverview/': DeviceOverview,
  'inventoree/deviceSummary/': DeviceSummary,
  'inventoree/deviceDetails/': DeviceConfiguration,
  'inventoree/deviceAttached/': DeviceAttached,
  'inventoree/devicePrinters/': DevicePrinters,
  'inventoree/deviceAlarms/': DevicePerformance,
  'inventoree/deviceWarrantyAndFiles/': DeviceWarranty,
  'inventoree/deviceEvents/': DeviceHardwareEvents,
  'inventoree/deviceProperties/': DeviceProperties,
  'inventoree/deviceSettings/': DeviceSettings,
  'inventoree/deviceSubSettings/': DeviceSubSettings,
  'inventoree/deviceSoftwareLicenses/': DeviceSoftwareLicenses,
  'cmdb/deviceView/': DeviceCmdb,
  'overview/agentSettings/': DeviceAgentSettings,
  'overview/deviceModulesSettings/': DeviceAgentModules,
  'softsee/deviceSummary/': DeviceSoftseeSummary,
  'softsee/deviceEvents/': DeviceSoftseeEvents,
  'softsee/usedApplications/': DeviceSoftseeUsedApps,
  'opteemum/osUpdates/': DeviceSoftseeOs,
  'opteemum/applicationManagement/': DeviceSoftseeApps,
  'softsee/deviceApplications/': DeviceSoftseeApps,
  'softsee/installApplications/': DeviceSoftseeInstall,
  'opteemum/installApplications/': DeviceOpteemumInstall,
  'softsee/updateApplications/': DeviceSoftseeUpdates,
  'opteemum/updateApplications/': DeviceOpteemumUpdates,
  'softsee/uninstallApplications/': DeviceSoftseeUninstall,
  'softsee/devicePackages/': DeviceSoftseePackages,
  'opteemum/deviceSummary/': DeviceOpteemumSummary,
  'opteemum/osPatchManagement/': DeviceOsManagement,
  'opteemum/remoteDriverErrors/': DeviceDriverErrors,
  'opteemum/startupServices/': DeviceStartupServices,
  'opteemum/startupApps/': DeviceStartupApps,
  'opteemum/remoteEventViewer/': DeviceRemoteEventViewer,
  'opteemum/deviceEvents/': DeviceOpteemumEvents,
  'proactivitee/deviceSummary/': DeviceProactiviteeSummary,
  'inventoree/agentlessDetails/': AgentlessDetails,
  'inventoree/deviceUpload/': DeviceUpload,
  'inventoree/agentlessSettings/': DeviceAgentless,
  'inventoree/deviceQuickDetails/': DeviceQuickDetails,
  'softsee/deviceQuickDetails/': DeviceSoftseeQuickDetails,
  'inventoree/deviceEndpointPerformanceSettings/': DeviceEndpointPerformanceSettings,
  'opteemum/cmd/': DeviceCmd,
  'opteemum/commandPrompt/': DeviceCmd,
  'opteemum/setupProtection/': DeviceSetupProtection,
  'opteemum/tuneUp/': DeviceTuneUp,
  'opteemum/diagnosisRepairProtection/': DeviceDiagnosisRepair,
  'opteemum/restoreOptimize/': DeviceRestoreOptimize,
  'opteemum/virusRemovalProtection/': DeviceVirusRemoval,
  'proactivitee/deviceAlarms/': DeviceSystemAlarms,
  'proactivitee/deviceEvents/': DeviceProactiviteeEvents,
  'proactivitee/deviceRemoteEvents/': DeviceProactiviteeEventViewer,
  'proactivitee/externalMonitoring/': DeviceExternalMonitoring,
  'opteemum/runningApplications/': DeviceActiveApps,
  'opteemum/processLoad/': DeviceProcessLoad,
  'opteemum/currentSessions/': DeviceSessions,
  'proactivitee/extendedMonitoring/': DeviceExtendedMonitoring,
  'proactivitee/configRelay/': DeviceConfigRelay,
  'proactivitee/hardwareMonitoring/': DeviceSystemPerformance,
  'opteemum/performanceStatus/': DevicePerformanceManagement,
  'opteemum/networkStatus/': DeviceNetworkStatus,
  'proactivitee/neighborWatch/': DeviceNeighborWatch,
  'proactivitee/connectionQuality/': DeviceConnectionQuality,
  'proactivitee/coreServicesMonitoring/': DeviceCoreServices,
  'opteemum/remoteActions/': DeviceRemoteOps,
  'proactivitee/snmpSettings/': DeviceWatch,
  'opteemum/deviceHddDetails/': DeviceDiskManagement,
  'opteemum/userAccountSettings/': DeviceUac,
  /* Filele Remote Control ale tichetului (it_easeedesk_service_tabs.tab_url = remote_control/get*Commands). */
  ...Object.fromEntries(REMOTE_CONTROL_CATEGORIES.map((key) => [`remote_control/${key}/`, remoteControlCategory(key)])),
  'changes/deviceTodo/': DeviceAiTodo,
}

export function renderScreen(href: string, label: string, props: Omit<ScreenProps, 'screen'>): ReactNode {
  const Screen = SCREENS[href]
  const screen = (h: string, l: string) => renderScreen(h, l, props)
  if (!Screen) return <NotYetMoved id={props.id} href={href} label={label} />
  return <Screen {...props} screen={screen} />
}
